/* eslint-disable @typescript-eslint/no-explicit-any */
import { ApolloLink } from '@apollo/client';

export interface HeaderFilterOptions {
  include?: Array<string | RegExp>;
  exclude?: Array<string | RegExp>;
}

interface Headers {
  [key: string]: string;
}

const defaultForwardHeadersOptions = (): HeaderFilterOptions => ({
  include: [
    /ctx-\S+/i,
    /edge-\S+/i,
    /x-akamai-\S+/i,
    /x-forwarded-\S+/i,
    'authorization',
    'client-ip',
    'connection',
    'cookie',
    'ctx-site-locale',
    'debug-trace',
    'device-user-agent-id',
    'message-id',
    'parent-client-info',
    'parent-message-id',
    'trace-id',
    'user-agent',
    'x-abov',
    'referer',
    'x-eb-client',
  ],
});

export const forwardHeadersLink = (requestHeaders: Headers) => {
  /**
   * Cache the results of the first pass that goes through the link chain.
   * Each incoming network request should have a separate instance of ApolloClient
   * and corresponding link chain, so no concern about request bleeding.
   */
  let forwardHeaders: Record<string, unknown>;

  return new ApolloLink((operation, forward) => {
    const { include, exclude } = defaultForwardHeadersOptions();

    if (requestHeaders && (include || exclude)) {
      operation.setContext(({ headers }: Record<string, any>) => {
        if (!forwardHeaders) {
          const headerKeys = Object.keys(requestHeaders).filter((name) => {
            const matchInclude = Array.isArray(include) && include.length ? matchHeader(name, include) : true;

            // if the header fails the include check, then it will be omitted/excluded anyway, so bail out here
            if (!matchInclude) {
              return false;
            }

            // otherwise, check for negative match against the exclude list
            const matchExclude = Array.isArray(exclude) && exclude.length ? matchHeader(name, exclude) : false;

            return !matchExclude;
          });

          forwardHeaders = headerKeys.reduce((obj: Record<string, any>, header: string) => {
            obj[header] = requestHeaders[header] || null;

            return obj;
          }, {});
        }

        return {
          headers: {
            ...headers,
            ...forwardHeaders,
          },
        };
      });
    }

    return forward(operation);
  });
};

/**
 * @private
 *
 * Internal function for evaluating a header name against a list of checks.
 *
 * @param name the header name (i.e. the "key" in the key/value pair)
 * @param checks an array of checks to test against `name`
 */
export const matchHeader = (name: string, checks: Array<string | RegExp>): boolean => {
  if (!checks || !checks.length) {
    return false;
  }

  return checks.some((check) => {
    if (check instanceof RegExp) {
      return check.test(name);
    }

    return name === check;
  });
};

/**
 * Combine two HeaderFilterOptions sets
 *
 * @param primary the main or default set of HeaderFilterOptions
 * @param secondary the optional or supplementary set of HeaderFilterOptions to merge in
 */

export const combineHeaderFilterOptions = (
  primary: HeaderFilterOptions,
  secondary: HeaderFilterOptions,
): HeaderFilterOptions => {
  const filterOptions: HeaderFilterOptions = {};

  if (primary.include || secondary.include) {
    filterOptions.include = [...(primary.include || []), ...(secondary.include || [])];
  }

  if (primary.exclude || secondary.exclude) {
    filterOptions.exclude = [...(primary.exclude || []), ...(secondary.exclude || [])];
  }

  return filterOptions;
};
